import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Text,
  VStack,
  HStack,
  Badge,
  Spinner,
  Flex,
  IconButton,
  Dialog,
  Grid,
  GridItem,
} from '@chakra-ui/react';
import { FiX, FiBell, FiCheck, FiEdit, FiUpload, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { dataService } from '../services/dataService';
import { useAuth } from '../context/AuthContext';

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Filter notifications based on role
  const filterNotificationsByRole = useCallback((notifications) => {
    if (!user || !user.role) return notifications;

    const userRole = user.role.toLowerCase();

    return notifications.filter(notif => {
      const message = notif.message.toLowerCase();

      if (userRole === 'user') {
        return message.includes('approved') || message.includes('edited') || message.includes('updated');
      } else if (userRole === 'editor' || userRole === 'admin') {
        return true;
      }

      return false;
    });
  }, [user]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await dataService.getNotifications();
      const filteredNotifications = filterNotificationsByRole(response.data);
      const unread = filteredNotifications.filter(notif => !notif.is_read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [filterNotificationsByRole]);

  // Fetch notifications
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await dataService.getNotifications();
      const notificationsWithParsedChanges = response.data.map(notif => ({
        ...notif,
        changes_json: notif.changes_json
          ? (typeof notif.changes_json === 'string' ? JSON.parse(notif.changes_json) : notif.changes_json)
          : []
      }));

      const filteredNotifications = filterNotificationsByRole(notificationsWithParsedChanges);
      setNotifications(filteredNotifications);

      const filteredUnreadCount = filteredNotifications.filter(notif => !notif.is_read).length;
      setUnreadCount(filteredUnreadCount);

      // Auto-select first notification if available
      if (filteredNotifications.length > 0 && !selectedNotification) {
        setSelectedNotification(filteredNotifications[0]);
      }

    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await dataService.markNotificationAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId ? { ...notif, is_read: true } : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    setSelectedNotification(notification);
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const unreadNotificationIds = notifications
        .filter(notif => !notif.is_read)
        .map(notif => notif.id);

      if (unreadNotificationIds.length > 0) {
        await dataService.markAllNotificationsAsRead();
      }

      setNotifications(prev =>
        prev.map(notif => ({ ...notif, is_read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Toggle modal
  const toggleModal = () => {
    if (!isOpen) {
      fetchNotifications();
    } else {
      setSelectedNotification(null);
    }
    setIsOpen(!isOpen);
  };

  // Poll for new notifications
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Format date
  const formatDate = (notification, field = 'created_at') => {
    if (field === 'created_at' && notification.formatted_created_at) {
      return notification.formatted_created_at;
    }
    if (field === 'edit_timestamp' && notification.formatted_edit_timestamp) {
      return notification.formatted_edit_timestamp;
    }

    const dateString = field === 'edit_timestamp' ? notification.edit_timestamp : notification.created_at;
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      const year = date.getFullYear();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const day = date.getDate();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${month} ${day}, ${year}, ${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return dateString;
    }
  };

  // Get notification type and icon
  const getNotificationTypeInfo = (message) => {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('new agent created')) {
      return { type: 'agent_created', icon: FiUpload, color: 'blue', label: 'New Agent' };
    } else if (lowerMessage.includes('new file uploaded')) {
      return { type: 'file_uploaded', icon: FiUpload, color: 'green', label: 'Upload' };
    } else if (lowerMessage.includes('batch approved')) {
      return { type: 'batch_approved', icon: FiCheckCircle, color: 'green', label: 'Approved' };
    } else if (lowerMessage.includes('edited') || lowerMessage.includes('updated')) {
      return { type: 'edit', icon: FiEdit, color: 'orange', label: 'Edited' };
    } else if (lowerMessage.includes('deleted')) {
      return { type: 'deleted', icon: FiAlertCircle, color: 'red', label: 'Deleted' };
    } else {
      return { type: 'general', icon: FiBell, color: 'gray', label: 'Info' };
    }
  };

  // Render notification detail panel
  const renderDetailPanel = () => {
    if (!selectedNotification) {
      return (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="full"
          color="gray.400"
          p={6}
        >
          <FiBell size={48} />
          <Text mt={4} fontSize="md" textAlign="center">
            Chọn một thông báo để xem chi tiết
          </Text>
        </Flex>
      );
    }

    const typeInfo = getNotificationTypeInfo(selectedNotification.message);
    const changes = selectedNotification.changes_json || [];
    const hasChanges = changes && changes.length > 0;

    return (
      <Box p={5} h="full" overflowY="auto">
        {/* Header */}
        <HStack mb={4} gap={3}>
          <Box
            p={2}
            borderRadius="lg"
            bg={`${typeInfo.color}.100`}
            color={`${typeInfo.color}.600`}
          >
            <typeInfo.icon size={20} />
          </Box>
          <Box>
            <Badge colorPalette={typeInfo.color} mb={1}>{typeInfo.label}</Badge>
            <Text fontSize="xs" color="gray.500">
              {formatDate(selectedNotification, selectedNotification.edit_timestamp ? 'edit_timestamp' : 'created_at')}
            </Text>
          </Box>
        </HStack>

        {/* Message */}
        <Box mb={5} p={4} bg="gray.50" borderRadius="lg">
          <Text fontSize="sm" color="gray.700" lineHeight="tall">
            {selectedNotification.message}
          </Text>
        </Box>

        {/* Agent Info */}
        {(selectedNotification.agent_name || selectedNotification.country) && (
          <Box mb={5}>
            <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
              Agent Information
            </Text>
            <VStack align="stretch" gap={2} bg="white" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
              {selectedNotification.agent_name && (
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">Name</Text>
                  <Text fontSize="sm" fontWeight="medium">{selectedNotification.agent_name}</Text>
                </HStack>
              )}
              {selectedNotification.country && (
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">Country</Text>
                  <Text fontSize="sm" fontWeight="medium">{selectedNotification.country}</Text>
                </HStack>
              )}
              {selectedNotification.agent_id && (
                <HStack justify="space-between">
                  <Text fontSize="xs" color="gray.500">ID</Text>
                  <Text fontSize="sm" fontWeight="medium">#{selectedNotification.agent_id}</Text>
                </HStack>
              )}
            </VStack>
          </Box>
        )}

        {/* Editor Info */}
        <Box mb={5}>
          <Text fontWeight="semibold" fontSize="sm" color="gray.600" mb={2}>
            Performed By
          </Text>
          <HStack p={3} bg="purple.50" borderRadius="md">
            <Box
              w={8} h={8}
              borderRadius="full"
              bg="purple.500"
              color="white"
              display="flex"
              alignItems="center"
              justifyContent="center"
              fontWeight="bold"
              fontSize="sm"
            >
              {selectedNotification.editor_name?.charAt(0)?.toUpperCase() || '?'}
            </Box>
            <Text fontSize="sm" fontWeight="medium">{selectedNotification.editor_name || 'Unknown'}</Text>
          </HStack>
        </Box>

        {/* Changes Made (for edit type) */}
        {typeInfo.type === 'edit' && (
          <Box>
            <HStack mb={2}>
              <Text fontWeight="semibold" fontSize="sm" color="gray.600">
                Changes Made
              </Text>
              {hasChanges && (
                <Badge colorPalette="purple" size="sm">
                  {changes.length} field{changes.length > 1 ? 's' : ''}
                </Badge>
              )}
            </HStack>

            {!hasChanges ? (
              <Box p={4} bg="gray.50" borderRadius="md" textAlign="center">
                <Text fontSize="sm" color="gray.500">No change details available</Text>
              </Box>
            ) : (
              <VStack align="stretch" gap={3}>
                {changes.map((change, index) => (
                  <Box key={index} p={3} bg="white" borderRadius="md" border="1px solid" borderColor="gray.200">
                    <Text fontSize="xs" fontWeight="semibold" color="purple.600" mb={2}>
                      {change.field}
                    </Text>
                    <Grid templateColumns="1fr auto 1fr" gap={2} alignItems="center">
                      <Box p={2} bg="red.50" borderRadius="md">
                        <Text fontSize="xs" color="red.600" mb={1}>From</Text>
                        <Text fontSize="sm" color="gray.700" wordBreak="break-word">
                          {change.oldValue || '(empty)'}
                        </Text>
                      </Box>
                      <Text color="gray.400" fontSize="lg">→</Text>
                      <Box p={2} bg="green.50" borderRadius="md">
                        <Text fontSize="xs" color="green.600" mb={1}>To</Text>
                        <Text fontSize="sm" color="gray.700" fontWeight="medium" wordBreak="break-word">
                          {change.newValue || '(empty)'}
                        </Text>
                      </Box>
                    </Grid>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box position="relative">
      {/* Bell Button */}
      <IconButton
        variant="ghost"
        aria-label="Notifications"
        onClick={toggleModal}
        position="relative"
        fontSize="xl"
        _hover={{ bg: 'purple.100' }}
      >
        <FiBell size={20} />
      </IconButton>

      {/* Badge */}
      {unreadCount > 0 && (
        <Badge
          position="absolute"
          top="-2px"
          right="-2px"
          colorPalette="red"
          borderRadius="full"
          fontSize="xs"
          minW="18px"
          h="18px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          zIndex={1}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </Badge>
      )}

      {/* Modal Dialog */}
      {isOpen && createPortal(
        <Dialog.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
          <Dialog.Backdrop
            bg="blackAlpha.600"
            backdropFilter="blur(4px)"
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={9999}
          />
          <Dialog.Positioner
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10000}
          >
            <Dialog.Content
              maxW="900px"
              w="95vw"
              h="75vh"
              maxH="700px"
              borderRadius="2xl"
              overflow="hidden"
              boxShadow="2xl"
              zIndex={10001}
              bg="white"
            >
              {/* Header */}
              <Dialog.Header
                bg="purple.600"
                color="white"
                py={4}
                px={6}
              >
                <Flex justify="space-between" align="center" w="full">
                  <HStack gap={3}>
                    <FiBell size={24} />
                    <Text fontWeight="bold" fontSize="lg">
                      Notifications
                    </Text>
                    {unreadCount > 0 && (
                      <Badge colorPalette="red" borderRadius="full" px={2}>
                        {unreadCount} new
                      </Badge>
                    )}
                  </HStack>
                  <HStack gap={2}>
                    {unreadCount > 0 && (
                      <Button
                        size="xs"
                        variant="outline"
                        color="white"
                        borderColor="whiteAlpha.500"
                        _hover={{ bg: 'whiteAlpha.200' }}
                        onClick={markAllAsRead}
                      >
                        <FiCheck /> Mark all read
                      </Button>
                    )}
                    <IconButton
                      size="sm"
                      variant="ghost"
                      color="white"
                      _hover={{ bg: 'whiteAlpha.200' }}
                      onClick={() => setIsOpen(false)}
                      aria-label="Close"
                    >
                      <FiX />
                    </IconButton>
                  </HStack>
                </Flex>
              </Dialog.Header>

              {/* Content - Split Pane */}
              <Dialog.Body p={0} h="calc(100% - 72px)">
                {loading ? (
                  <Flex justify="center" align="center" h="full">
                    <Spinner size="lg" color="purple.500" mr={3} />
                    <Text color="gray.500">Loading notifications...</Text>
                  </Flex>
                ) : notifications.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" h="full">
                    <Text fontSize="5xl">📭</Text>
                    <Text color="gray.500" fontWeight="medium" mt={3}>No notifications yet</Text>
                    <Text color="gray.400" fontSize="sm">You're all caught up!</Text>
                  </Flex>
                ) : (
                  <Grid templateColumns="350px 1fr" h="full">
                    {/* Left Panel - Notification List */}
                    <GridItem
                      borderRight="1px solid"
                      borderColor="gray.200"
                      overflowY="auto"
                      bg="gray.50"
                    >
                      <VStack gap={0} align="stretch">
                        {notifications.map((notification) => {
                          const typeInfo = getNotificationTypeInfo(notification.message);
                          const isSelected = selectedNotification?.id === notification.id;

                          return (
                            <Box
                              key={notification.id}
                              px={4}
                              py={3}
                              cursor="pointer"
                              bg={isSelected ? 'purple.100' : notification.is_read ? 'transparent' : 'white'}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                              borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
                              borderLeftColor={isSelected ? 'purple.500' : 'transparent'}
                              _hover={{ bg: isSelected ? 'purple.100' : 'gray.100' }}
                              transition="all 0.15s"
                              onClick={() => handleNotificationClick(notification)}
                            >
                              <HStack align="flex-start" gap={3}>
                                <Box
                                  p={1.5}
                                  borderRadius="md"
                                  bg={`${typeInfo.color}.100`}
                                  color={`${typeInfo.color}.600`}
                                  flexShrink={0}
                                >
                                  <typeInfo.icon size={14} />
                                </Box>
                                <Box flex={1} minW={0}>
                                  <Text
                                    fontSize="sm"
                                    color="gray.800"
                                    fontWeight={notification.is_read ? 'normal' : 'semibold'}
                                    noOfLines={2}
                                    lineHeight="short"
                                  >
                                    {notification.message}
                                  </Text>
                                  <HStack mt={1.5} gap={2}>
                                    <Text fontSize="xs" color="gray.400">
                                      {formatDate(notification, notification.edit_timestamp ? 'edit_timestamp' : 'created_at')}
                                    </Text>
                                  </HStack>
                                </Box>
                                {!notification.is_read && (
                                  <Box
                                    w="8px"
                                    h="8px"
                                    bg="purple.500"
                                    borderRadius="full"
                                    flexShrink={0}
                                    mt={1}
                                  />
                                )}
                              </HStack>
                            </Box>
                          );
                        })}
                      </VStack>
                    </GridItem>

                    {/* Right Panel - Detail View */}
                    <GridItem bg="white" overflowY="auto">
                      {renderDetailPanel()}
                    </GridItem>
                  </Grid>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>,
        document.body
      )}
    </Box>
  );
};

export default NotificationBell;
